import { PaperPlaneTilt } from '@phosphor-icons/react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { ICliJob } from 'cli/types';
import { Button } from 'core/components';
import { AccessPermissions, ApiStatus, NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { useAgGridTableContext, useNotificationContext } from 'core/states';
import { getLocalDateForServer } from 'core/utils';
import { VendorQuoteHelper } from 'quotes/helpers';
import { useQuoteActions } from 'quotes/hooks';
import { IBulkSendAsSpaResponse, IVendorQuote } from 'quotes/types';
import { Color, Size, TooltipPosition, Variant } from 'theme/enums';

import { BulkSendAsSpaWarning } from './BulkSendAsSpaWarning';
import { IBulkSendAsSpaResult } from './types';
import {
  categorizeBulkSendAsSpaSelection,
  getBulkSendAsSpaConcurrentSendNotice,
  getBulkSendAsSpaSuccessMessages,
  getSendAsSpaStatusWriteFailedMessage,
  isBulkSendAsSpaConfirmationNeeded,
} from './utils';

export const BulkSendAsSpaButton = () => {
  // Set while the dialog is open; the day is taken at click time and reused for the send.
  const [confirmation, setConfirmation] = useState<{
    localDate: string;
    pastValidUntilQuoteIds: number[];
  } | null>(null);
  const [isSending, setIsSending] = useState(false);
  // Resends the BE could not version at enqueue time; reported with the job's toast.
  const droppedQuoteIdsRef = useRef<number[]>([]);
  // The rows sent, kept to name failed quotes after the refresh and deselect.
  const sentQuotesRef = useRef<IVendorQuote[]>([]);

  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();
  const { selectedRows, deselectAllRows, gridApi } = useAgGridTableContext();
  const { showNotification } = useNotificationContext();
  const { handleCliJob } = useCliJobHandler();
  const { sendBulkVendorQuoteAsSpa } = useQuoteActions();

  const { firstSends, resends, blockers, omits, alreadySent } = useMemo(
    () => categorizeBulkSendAsSpaSelection(selectedRows as IVendorQuote[], user),
    [selectedRows, user]
  );

  const eligible = useMemo(() => [...firstSends, ...resends], [firstSends, resends]);

  const isDisabled = eligible.length + blockers.length === 0;

  // When disabled purely because every selectable row is sent and unchanged, say so rather than the
  // generic "select an imported alternate VQ" — but only when nothing was omitted, so a mixed
  // already-sent + non-alternate selection doesn't misreport as "all already sent".
  const disabledTooltip =
    alreadySent.length > 0 && omits.length === 0
      ? t('tooltipBulkSendAsSpaAllAlreadySent')
      : t('tooltipBulkSendAsSpaDisabled');

  const onSendFinished = (cliJob: ICliJob) => {
    setIsSending(false);

    // Refresh the list so every affected row shows its new trade_agreement_status/error, whether
    // the batch was accepted (Sent) or rejected (Failed) — the BE writes both before the job
    // finishes. Server-side refresh mirrors UpdateQuotesButton (this is a server-model grid).
    gridApi?.refreshServerSide();

    const result = cliJob.result as IBulkSendAsSpaResult | null;
    const droppedCount = droppedQuoteIdsRef.current.length;
    const hasLeftOutQuotes = droppedCount > 0 || (result?.lockFailedQuoteIds?.length ?? 0) > 0;

    const statusWriteFailedMessage = getSendAsSpaStatusWriteFailedMessage({
      result,
      isBulk: true,
      quotes: sentQuotesRef.current,
      t,
    });

    if (statusWriteFailedMessage) {
      showNotification({
        type: ApiStatus.ERROR,
        title: t('titleError'),
        message: statusWriteFailedMessage,
        position: NotificationPosition.TOP,
      });
      deselectAllRows();

      return;
    }

    if (result?.sent === true) {
      const message = getBulkSendAsSpaSuccessMessages({
        result,
        droppedCount,
        fallbackSentCount: eligible.length,
      })
        .map(({ key, count }) => t(key, { count }))
        .join(' ');

      showNotification({
        type: hasLeftOutQuotes ? ApiStatus.WARNING : ApiStatus.SUCCESS,
        title: t(hasLeftOutQuotes ? 'titleWarning' : 'titleSuccess'),
        message,
        position: NotificationPosition.TOP,
      });

      // Flintfox has no dedup — clear the selection so an accidental re-click can't resend the
      // same batch (mirrors the single flow's auto-close).
      deselectAllRows();

      return;
    }

    const concurrentSendNotice = getBulkSendAsSpaConcurrentSendNotice(result);

    // The combined send succeeds or fails as a unit — prefer the BE error, else
    // point at the step that failed (mfr_part | trade_agreement).
    let message = t('messageBulkSendAsSpaError');
    if (concurrentSendNotice) {
      message = concurrentSendNotice.messages.map(({ key, count }) => t(key, { count })).join(' ');
    } else if (result?.error) {
      message = result.error;
    } else if (result?.failedStep) {
      message = t('messageBulkSendAsSpaErrorAtStep', {
        step: result.failedStep.replaceAll('_', ' '),
      });
    }

    showNotification({
      type: concurrentSendNotice?.isWarning ? ApiStatus.WARNING : ApiStatus.ERROR,
      title: t(concurrentSendNotice?.isWarning ? 'titleWarning' : 'titleError'),
      message:
        droppedCount > 0
          ? `${message} ${t('messageBulkSendAsSpaDropped', { count: droppedCount })}`
          : message,
      position: NotificationPosition.TOP,
    });
  };

  const onSendStopped = (cliJob: ICliJob) => {
    setIsSending(false);

    // A stopped/crashed job may still have written Failed statuses before dying; refresh so the
    // list doesn't keep showing the pre-send values.
    gridApi?.refreshServerSide();

    showNotification({
      type: ApiStatus.ERROR,
      title: t('titleError'),
      message: cliJob.statusDescription || t('messageBulkSendAsSpaError'),
      position: NotificationPosition.TOP,
    });
  };

  const onSend = (localDate: string) => {
    setConfirmation(null);
    setIsSending(true);
    droppedQuoteIdsRef.current = [];
    sentQuotesRef.current = eligible;

    // Each send pushes to Flintfox, so block re-click while in flight. handleCliJob swallows an
    // enqueue-time throw without hitting the terminal callbacks, so reset the guard on that path
    // too (the callbacks reset it once the job reaches a terminal status).
    const enqueue = async (payload: { quoteIds: number[]; localDate: string }) => {
      try {
        const response: IBulkSendAsSpaResponse = await sendBulkVendorQuoteAsSpa(payload);

        droppedQuoteIdsRef.current = response?.droppedQuoteIds ?? [];

        return response?.cliJob;
      } catch (error) {
        setIsSending(false);
        throw error;
      }
    };

    handleCliJob(
      enqueue,
      { quoteIds: eligible.map((quote) => quote.id), localDate },
      {
        cliJobInProgressMessage: t('messageBulkSendAsSpaInProgress'),
        cliJobErrorMessage: t('messageBulkSendAsSpaError'),
        notificationPosition: NotificationPosition.TOP,
        onCliJobFinished: onSendFinished,
        onCliJobStopped: onSendStopped,
        // A second send while this one still runs is skipped by the BE per-quote send lock.
        onCliJobPollFailed: ({ showLostTrackNotice }) => {
          setIsSending(false);
          gridApi?.refreshServerSide();
          showLostTrackNotice();
        },
        preferBackendError: true,
      }
    );
  };

  // Quotes with a past Valid Until are named in the dialog and still sent on confirmation.
  const onClick = () => {
    const localDate = getLocalDateForServer();
    const pastValidUntilQuoteIds = VendorQuoteHelper.getPastValidUntilQuoteIds(eligible, localDate);

    if (
      !isBulkSendAsSpaConfirmationNeeded(
        { blockers, omits, alreadySent, resends },
        pastValidUntilQuoteIds
      )
    ) {
      onSend(localDate);

      return;
    }

    setConfirmation({ localDate, pastValidUntilQuoteIds });
  };

  // Role-resource gated (opt-in per role); hidden entirely until an admin grants Send as SPA.
  if (!hasPermission(AccessPermissions.SEND_AS_SPA)) {
    return null;
  }

  return (
    <>
      <Button
        text={t('buttonLabelSendAsSpa')}
        onClick={onClick}
        disabled={isDisabled || isSending}
        variant={Variant.OUTLINED}
        startDecorator={<PaperPlaneTilt />}
        tooltipProps={
          isDisabled
            ? {
                placement: TooltipPosition.TOP,
                color: Color.NEUTRAL,
                size: Size.SM,
                title: disabledTooltip,
                children: null,
              }
            : undefined
        }
        // MUI swallows hover on disabled buttons; re-enable pointer events so the tooltip shows.
        sx={
          isDisabled
            ? {
                pointerEvents: 'auto !important',
                cursor: 'not-allowed !important',
                '&:hover': { backgroundColor: 'transparent !important' },
              }
            : undefined
        }
      />

      {confirmation && (
        <BulkSendAsSpaWarning
          blockers={blockers}
          omits={omits}
          alreadySent={alreadySent}
          firstSends={firstSends}
          resends={resends}
          pastValidUntilQuoteIds={confirmation.pastValidUntilQuoteIds}
          onSend={() => onSend(confirmation.localDate)}
          onCancel={() => setConfirmation(null)}
        />
      )}
    </>
  );
};
